import {
  useEffect, createContext, useMemo, useContext,
} from 'react'
import Router from 'next/router'
import Script from 'next/script'

export function GAScript( { gaTrackingId, awaitConsent = false, debugMode = false } ) {
  if ( !gaTrackingId ) {
    return null
  }

  const deferForConsent = awaitConsent
    ? `
    gtag('consent', 'default', {
      'ad_storage': 'denied',
      'analytics_storage': 'denied',
    });
    `
    : ''

  return (
    <>
      <Script
        id="gtag"
        async
        src={ `https://www.googletagmanager.com/gtag/js?id=${ gaTrackingId }` }
      />
      <Script
        id="ga-tracking"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={ {
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            ${ deferForConsent }
            gtag('js', new Date());
            gtag('config', '${ gaTrackingId }', { 
              debug_mode: ${ debugMode },
              send_page_view: false
            });
          `,
        } }
      />
    </>
  )
}

const GAContext = createContext( { gaTrackingId: null } )
GAContext.displayName = 'GAContext'

export function GAProvider( { gaTrackingId, children } ) {
  const value = useMemo( () => ( { gaTrackingId } ), [gaTrackingId] )

  useEffect( () => {
    if ( gaTrackingId ) {
      const handleRouteChange = () => {
        pageView()
      }

      Router.events.on( 'routeChangeComplete', handleRouteChange )

      return () => {
        Router.events.off( 'routeChangeComplete', handleRouteChange )
      }
    }
  }, [gaTrackingId] )

  return (
    <GAContext.Provider value={ value }>
      { children }
      <GAScript
        gaTrackingId={ gaTrackingId }
        awaitConsent
      />
    </GAContext.Provider>
  )
}

export const useGA = () => {
  const { gaTrackingId } = useContext( GAContext )

  return {
    gaTrackingId,
    pageView,
    event,
    grantAnalytics,
    denyAnalytics,
  }
}

// https://developers.google.com/analytics/devguides/collection/gtagjs/pages
export const pageView = () => {
  if ( typeof window !== 'undefined' && window.gtag ) {
    window.gtag( 'event', 'page_view' )
  }
}

// https://developers.google.com/analytics/devguides/collection/gtagjs/events
export const event = ( {
  action, category, label, value,
} ) => {
  if ( typeof window !== 'undefined' && window.gtag ) {
    window.gtag( 'event', action, {
      event_category: category,
      event_label: label,
      value,
    } )
  }
}

export const grantAnalytics = () => {
  if ( typeof window !== 'undefined' && window.gtag ) {
    window.gtag( 'consent', 'update', {
      analytics_storage: 'granted',
    } )
  }
}

export const denyAnalytics = () => {
  if ( typeof window !== 'undefined' && window.gtag ) {
    window.gtag( 'consent', 'update', {
      analytics_storage: 'denied',
    } )
  }
}
